import { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Upload, FileText, Calendar } from 'lucide-react';
import { PageWrapper } from '../components/PageWrapper';
import { Button } from '../components/ui/button';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { exportCSV, exportPDF, exportBackup } from '@/utils/export';
import { exportAllData, importAllData, formatDate, formatShortDate } from '@/utils/storage';
import { toast } from 'sonner';

export const Export = () => {
  const today = new Date();
  const last7Days = new Date(today);
  last7Days.setDate(last7Days.getDate() - 7);
  const last30Days = new Date(today);
  last30Days.setDate(last30Days.getDate() - 30);

  const [dateRange, setDateRange] = useState('7');
  const [customStart, setCustomStart] = useState(last7Days);
  const [customEnd, setCustomEnd] = useState(today);
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);

  const [isExporting, setIsExporting] = useState(false);

  const getDateRange = () => {
    if (dateRange === '7') {
      return { start: formatDate(last7Days), end: formatDate(today) };
    } else if (dateRange === '30') {
      return { start: formatDate(last30Days), end: formatDate(today) };
    } else {
      return { start: formatDate(customStart), end: formatDate(customEnd) };
    }
  };

  const handleExportCSV = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { start, end } = getDateRange();
      const result = await exportCSV(start, end);
      if (!result.native) {
        // Only show toast for web (native shows share sheet)
        toast.success('CSV exported successfully');
      }
    } catch (error) {
      console.error('CSV export error:', error);
      toast.error('Failed to export CSV');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPDF = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { start, end } = getDateRange();
      const result = await exportPDF(start, end);
      if (!result.native) {
        // Only show toast for web (native shows share sheet)
        toast.success('PDF exported successfully');
      }
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const handleBackup = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const data = exportAllData();
      const result = await exportBackup(data);
      if (!result.native) {
        // Only show toast for web (native shows share sheet)
        toast.success('Backup file created');
      }
    } catch (error) {
      console.error('Backup error:', error);
      toast.error('Failed to create backup');
    } finally {
      setIsExporting(false);
    }
  };

  const handleRestore = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result);
        importAllData(data);
        toast.success('Data restored successfully. Please refresh the app.');
        setTimeout(() => window.location.reload(), 1500);
      } catch (error) {
        toast.error('Invalid backup file');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  return (
    <PageWrapper className="bg-app">
      <div className="max-w-md mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-theme-light rounded-3xl p-6 shadow-ambient border border-theme-secondary mb-6"
        >
          <h2 className="font-body font-medium text-primary mb-4">Select Date Range</h2>
          
          <div className="space-y-3">
            <button
              onClick={() => setDateRange('7')}
              className={`w-full p-4 rounded-2xl text-left transition-all font-body ${
                dateRange === '7'
                  ? 'bg-wisteria-100 text-wisteria-900'
                  : 'bg-wisteria-50/50 text-secondary hover:bg-wisteria-50'
              }`}
              data-testid="range-7-days"
            >
              Last 7 days
            </button>
            
            <button
              onClick={() => setDateRange('30')}
              className={`w-full p-4 rounded-2xl text-left transition-all font-body ${
                dateRange === '30'
                  ? 'bg-wisteria-100 text-wisteria-900'
                  : 'bg-wisteria-50/50 text-secondary hover:bg-wisteria-50'
              }`}
              data-testid="range-30-days"
            >
              Last 30 days
            </button>
            
            <button
              onClick={() => setDateRange('custom')}
              className={`w-full p-4 rounded-2xl text-left transition-all font-body ${
                dateRange === 'custom'
                  ? 'bg-wisteria-100 text-wisteria-900'
                  : 'bg-wisteria-50/50 text-secondary hover:bg-wisteria-50'
              }`}
              data-testid="range-custom"
            >
              Custom range
            </button>
          </div>

          {dateRange === 'custom' && (
            <div className="mt-4 flex gap-3">
              <Popover open={showStartPicker} onOpenChange={setShowStartPicker}>
                <PopoverTrigger asChild>
                  <button
                    className="flex-1 p-3 rounded-xl bg-wisteria-50/50 text-left text-sm font-body text-secondary flex items-center gap-2"
                    data-testid="custom-start-date"
                  >
                    <Calendar size={16} />
                    {formatShortDate(formatDate(customStart))}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={customStart}
                    onSelect={(date) => {
                      if (date) {
                        setCustomStart(date);
                        setShowStartPicker(false);
                      }
                    }}
                    disabled={(date) => date > today}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              <span className="self-center text-secondary">to</span>

              <Popover open={showEndPicker} onOpenChange={setShowEndPicker}>
                <PopoverTrigger asChild>
                  <button
                    className="flex-1 p-3 rounded-xl bg-wisteria-50/50 text-left text-sm font-body text-secondary flex items-center gap-2"
                    data-testid="custom-end-date"
                  >
                    <Calendar size={16} />
                    {formatShortDate(formatDate(customEnd))}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <CalendarComponent
                    mode="single"
                    selected={customEnd}
                    onSelect={(date) => {
                      if (date) {
                        setCustomEnd(date);
                        setShowEndPicker(false);
                      }
                    }}
                    disabled={(date) => date > today || date < customStart}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </motion.div>

        {/* Export Options */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-theme-light rounded-3xl p-6 shadow-ambient border border-theme-secondary mb-6"
        >
          <h2 className="font-body font-medium text-primary mb-4">Export Format</h2>
          
          <div className="space-y-3">
            <button
              onClick={handleExportCSV}
              disabled={isExporting}
              className="w-full p-4 rounded-2xl bg-wisteria-50/50 transition-all hover:bg-wisteria-100 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="export-csv-btn"
            >
              <div className="w-10 h-10 rounded-full bg-wisteria-100 flex items-center justify-center">
                <FileText size={20} style={{ color: 'var(--theme-accent, #6B5B95)' }} />
              </div>
              <p className="font-body text-sm text-primary text-center">
                {isExporting ? 'Exporting CSV. Please wait.' : 'Export as CSV. Spreadsheet format.'}
              </p>
            </button>

            <button
              onClick={handleExportPDF}
              disabled={isExporting}
              className="w-full p-4 rounded-2xl bg-wisteria-50/50 transition-all hover:bg-wisteria-100 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="export-pdf-btn"
            >
              <div className="w-10 h-10 rounded-full bg-wisteria-100 flex items-center justify-center">
                <Upload size={20} style={{ color: 'var(--theme-accent, #6B5B95)' }} />
              </div>
              <p className="font-body text-sm text-primary text-center">
                {isExporting ? 'Exporting PDF. Please wait.' : 'Export as PDF. Printable document.'}
              </p>
            </button>
          </div>
        </motion.div>

        {/* Backup & Restore */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-theme-light rounded-3xl p-6 shadow-ambient border border-theme-secondary"
        >
          <h2 className="font-body font-medium text-primary mb-4">Backup & Restore</h2>
          
          <div className="space-y-3">
            <button
              onClick={handleBackup}
              disabled={isExporting}
              className="w-full p-4 rounded-2xl bg-wisteria-50/50 transition-all hover:bg-wisteria-100 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="backup-btn"
            >
              <div className="w-10 h-10 rounded-full bg-wisteria-100 flex items-center justify-center">
                <Upload size={20} style={{ color: 'var(--theme-accent, #6B5B95)' }} />
              </div>
              <p className="font-body text-sm text-primary text-center">
                {isExporting ? 'Creating backup. Please wait.' : 'Create backup. Export all data as JSON.'}
              </p>
            </button>

            <label className="w-full p-4 rounded-2xl bg-wisteria-50/50 transition-all hover:bg-wisteria-100 flex items-center justify-center gap-3 cursor-pointer">
              <div className="w-10 h-10 rounded-full bg-wisteria-100 flex items-center justify-center">
                <Download size={20} style={{ color: 'var(--theme-accent, #6B5B95)' }} />
              </div>
              <p className="font-body text-sm text-primary text-center">Restore from backup. Import JSON backup file.</p>
              <input
                type="file"
                accept=".json"
                onChange={handleRestore}
                className="hidden"
                data-testid="restore-input"
              />
            </label>
          </div>
        </motion.div>
      </div>
    </PageWrapper>
  );
};

export default Export;
